import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, Cake, Calendar as CalIcon, ClockCounterClockwise, Megaphone, Pulse, CaretRight } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import confetti from "canvas-confetti";

const COLORS = ["#0B2027", "#285943", "#C05A42", "#D4A373", "#6B7280"];

export default function Dashboard() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [summary, setSummary] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [peopleDialog, setPeopleDialog] = useState(null);
  const [absentee, setAbsentee] = useState(null);
  const [absHistory, setAbsHistory] = useState(null);
  const [absLoading, setAbsLoading] = useState(false);
  const confettiFired = useRef(false);

  const canAbsence = ["admin", "hr", "gm", "manager"].includes(user?.role);

  const openAbsHistory = async (uid, name) => {
    setAbsHistory({ name, loading: true });
    try {
      const { data } = await api.get(`/reports/absenteeism/history?user_id=${uid}`);
      setAbsHistory({ ...data, name });
    } catch {
      setAbsHistory({ name, leaves: [], by_month: [], total_days: 0 });
    }
  };

  // Doğum günü konfeti + banner — kullanıcı bu sayfayı ilk açtığında bir kez çalışsın
  useEffect(() => {
    if (!user?.is_birthday || confettiFired.current) return;
    // Bugün için daha önce gösterip göstermediğimizi hatırla
    const key = `bday_shown_${user.id}_${new Date().toISOString().slice(0, 10)}`;
    if (sessionStorage.getItem(key)) { confettiFired.current = true; return; }
    sessionStorage.setItem(key, "1");
    confettiFired.current = true;
    const duration = 5000;
    const end = Date.now() + duration;
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors: ["#ec4899", "#f59e0b", "#8b5cf6", "#10b981"] });
      confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors: ["#ec4899", "#f59e0b", "#8b5cf6", "#10b981"] });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
    // Büyük patlama
    setTimeout(() => confetti({ particleCount: 200, spread: 100, origin: { y: 0.4 } }), 400);
  }, [user?.is_birthday, user?.id]);

  const openPeople = async (kind, title) => {
    try {
      const { data } = await api.get(`/dashboard/people?kind=${kind}`);
      setPeopleDialog({ kind, title, data });
    } catch {
      setPeopleDialog({ kind, title, data: [] });
    }
  };

  useEffect(() => {
    if (user?.role !== "staff") {
      api.get("/reports/summary").then((r) => setSummary(r.data)).catch(() => {});
    }
    if (["admin", "hr", "gm", "manager"].includes(user?.role)) {
      setAbsLoading(true);
      api.get("/reports/absenteeism").then((r) => setAbsentee(r.data)).catch(() => {}).finally(() => setAbsLoading(false));
    }
    api.get("/announcements").then((r) => setAnnouncements(r.data.slice(0, 5))).catch(() => {});
    api.get("/calendar").then((r) => setCalendar(r.data)).catch(() => {});
    api.get("/leaves").then((r) => setLeaves(r.data.slice(0, 5))).catch(() => {});
  }, [user]);

  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
  const birthdays = calendar.filter((e) => e.kind === "birthday" && e.date?.slice(5, 10) === today.slice(5, 10));
  const upcomingHolidays = calendar.filter((e) => e.kind === "holiday" && e.date >= today).slice(0, 5);

  return (
    <div className="space-y-6">
      {user?.is_birthday && (
        <div
          className="relative overflow-hidden rounded-xl border border-pink-500/40 bg-gradient-to-r from-pink-500/20 via-amber-400/15 to-purple-500/20 p-6 md:p-8"
          data-testid="birthday-banner"
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 rounded-full bg-white/10 backdrop-blur p-3">
              <Cake size={40} weight="fill" className="text-pink-500 animate-bounce" />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.2em] text-pink-700 dark:text-pink-300 font-semibold">
                Colossae Hotel Ailesi
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold mt-1">
                Doğum Gününüz Kutlu Olsun, {user.first_name}!
              </h2>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed max-w-xl">
                Otelimiz adına en içten dileklerimizle: Sağlıklı, huzurlu ve başarılarla dolu bir yaş dilerken;
                Colossae Hotel ailesinin değerli bir üyesi olduğunuz için teşekkür ederiz. 🎂✨
              </p>
            </div>
          </div>
        </div>
      )}
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t("dash.eyebrow")}</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">{t("dash.welcome")}, {user?.first_name}</h1>
      </div>

      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users} label={t("dash.total")} value={summary.total} testid="stat-total" onClick={() => openPeople("total", t("dash.total"))} />
          <StatCard icon={ClockCounterClockwise} label={t("dash.on_leave")} value={summary.on_leave_today} testid="stat-onleave" onClick={() => openPeople("on_leave", t("dash.on_leave"))} />
          <StatCard icon={ClockCounterClockwise} label={t("dash.pending_leaves")} value={summary.pending_leaves} testid="stat-pending" onClick={() => openPeople("pending_leaves", t("dash.pending_leaves"))} />
          <StatCard icon={Cake} label={t("dash.birthdays")} value={birthdays.length} testid="stat-birthdays" onClick={() => openPeople("birthdays_today", t("dash.birthdays"))} />
        </div>
      )}

      <Dialog open={!!peopleDialog} onOpenChange={(o) => !o && setPeopleDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{peopleDialog?.title}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {(peopleDialog?.data || []).length === 0 && <div className="text-sm text-muted-foreground">{t("common.no_records")}</div>}
            {(peopleDialog?.data || []).map((p, i) => (
              <div key={p.id || p.user_id || i} className="border-b border-border pb-2">
                <div className="font-semibold text-sm">{p.name || p.user_name}</div>
                <div className="text-xs text-muted-foreground">{p.department} {p.position ? `· ${p.position}` : ""} {p.start_date ? `· ${p.start_date?.slice(0,10)} → ${p.end_date?.slice(0,10)}` : ""} {p.birth_date ? `· Doğum: ${p.birth_date?.slice(5,10)}` : ""}</div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <div className="grid lg:grid-cols-3 gap-4">
        {summary && (
          <Card className="lg:col-span-2">
            <CardContent className="p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">{t("dash.dept_distribution")}</div>
              <div className="h-64">
                <ResponsiveContainer>
                  <BarChart data={Object.entries(summary.dept_counts).map(([name, value]) => ({ name, value }))}>
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#0B2027" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {summary && (
          <Card>
            <CardContent className="p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">{t("dash.age_distribution")}</div>
              <div className="h-64">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={Object.entries(summary.age_buckets).map(([name, value]) => ({ name, value }))} dataKey="value" nameKey="name" outerRadius={80}>
                      {Object.keys(summary.age_buckets).map((bucketName) => <Cell key={`age-${bucketName}`} fill={COLORS[Object.keys(summary.age_buckets).indexOf(bucketName) % COLORS.length]} />)}
                    </Pie>
                    <Tooltip /><Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3 flex items-center gap-2"><Megaphone size={14} /> {t("dash.latest_announcements")}</div>
            <div className="space-y-3">
              {announcements.length === 0 && <div className="text-sm text-muted-foreground">{t("dash.no_announcements")}</div>}
              {announcements.map((a) => (
                <div key={a.id} className="border-l-2 border-primary pl-3">
                  <div className="font-semibold text-sm">{a.title}</div>
                  <div className="text-xs text-muted-foreground line-clamp-2">{a.body}</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3 flex items-center gap-2"><CalIcon size={14} /> {t("dash.upcoming_holidays")}</div>
            <div className="space-y-2">
              {upcomingHolidays.length === 0 && <div className="text-sm text-muted-foreground">{t("dash.no_holidays")}</div>}
              {upcomingHolidays.map((e) => (
                <div key={e.id} className="flex justify-between text-sm">
                  <span>{e.title}</span>
                  <Badge variant="outline">{e.date?.slice(0, 10)}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3 flex items-center gap-2"><Cake size={14} /> Bugünkü Doğum Günleri</div>
            <div className="space-y-2">
              {birthdays.length === 0 && <div className="text-sm text-muted-foreground">{t("dash.no_birthdays")}</div>}
              {birthdays.map((b) => (
                <div key={b.id} className="text-sm">{b.title}</div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {canAbsence && (
        <Card data-testid="absenteeism-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2"><Pulse size={14} /> Devamsızlık · Bu Ay ({absentee?.month || ""})</div>
              <div className="flex items-center gap-1.5">
                {(absentee?.types || []).map((ty) => <Badge key={ty.key} variant="outline" className="text-[10px]">{ty.label}</Badge>)}
              </div>
            </div>

            {!absentee ? (
              <div className="text-sm text-muted-foreground py-6 text-center">{absLoading ? "Yükleniyor…" : "Veri yok"}</div>
            ) : (
              <>
                <div className="grid sm:grid-cols-3 gap-3 mb-5">
                  <div className="rounded-lg border border-border p-4">
                    <div className="text-xs text-muted-foreground">Devamsız personel</div>
                    <div className="font-display text-2xl font-bold mt-1" data-testid="abs-overall-personnel">{absentee.overall.personnel}</div>
                  </div>
                  <div className="rounded-lg border border-border p-4">
                    <div className="text-xs text-muted-foreground">Toplam devamsızlık günü</div>
                    <div className="font-display text-2xl font-bold mt-1" data-testid="abs-overall-days">{absentee.overall.days}</div>
                  </div>
                  <div className="rounded-lg border border-border p-4">
                    <div className="text-xs text-muted-foreground">Etkilenen departman</div>
                    <div className="font-display text-2xl font-bold mt-1">{absentee.by_department.length}</div>
                  </div>
                </div>

                <div className="grid lg:grid-cols-2 gap-6">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Son 12 Ay — Devamsızlık Günü</div>
                    <div className="h-56">
                      <ResponsiveContainer>
                        <BarChart data={absentee.trend.map((m) => ({ name: m.month.slice(2), "gün": m.days }))}>
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={24} />
                          <Tooltip />
                          <Bar dataKey="gün" fill="#C05A42" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">En Çok Devamsızlık (İlk 10) — detay için tıklayın</div>
                    {absentee.top.length === 0 ? (
                      <div className="text-sm text-muted-foreground py-8 text-center">Bu ay devamsızlık kaydı yok 🎉</div>
                    ) : (
                      <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                        {absentee.top.map((p, i) => (
                          <button key={p.user_id} type="button" onClick={() => openAbsHistory(p.user_id, p.name)} data-testid={`abs-top-${p.user_id}`}
                            className="w-full flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-left hover:border-primary transition-colors">
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate">{i + 1}. {p.name}</div>
                              <div className="text-[11px] text-muted-foreground truncate">{p.department} · {Object.entries(p.by_type).map(([k, v]) => `${k}: ${v}`).join(" · ")}</div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge variant="destructive" className="text-[11px]">{p.days} gün</Badge>
                              <CaretRight size={14} className="text-muted-foreground" />
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {absentee.by_department.length > 0 && (
                  <div className="mt-5">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Departman Bazında (Bu Ay)</div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {absentee.by_department.map((d) => (
                        <div key={d.department} className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2" data-testid={`abs-dept-${d.department}`}>
                          <span className="text-sm truncate">{d.department}</span>
                          <span className="text-xs text-muted-foreground">{d.personnel} kişi · <b className="text-foreground">{d.days} gün</b></span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={!!absHistory} onOpenChange={(o) => !o && setAbsHistory(null)}>
        <DialogContent className="max-w-lg" data-testid="abs-history-dialog">
          <DialogHeader><DialogTitle>Devamsızlık Geçmişi · {absHistory?.name}</DialogTitle></DialogHeader>
          {absHistory?.loading ? (
            <div className="text-sm text-muted-foreground py-6 text-center">Yükleniyor…</div>
          ) : (
            <div className="space-y-4">
              <div className="text-sm">Son 12 ayda toplam <b>{absHistory?.total_days || 0}</b> gün · {(absHistory?.leaves || []).length} kayıt</div>
              {(absHistory?.by_month || []).some((m) => m.days > 0) && (
                <div className="h-40">
                  <ResponsiveContainer>
                    <LineChart data={(absHistory.by_month).map((m) => ({ name: m.month.slice(2), "gün": m.days }))}>
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={24} />
                      <Tooltip />
                      <Line type="monotone" dataKey="gün" stroke="#C05A42" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
              <div className="max-h-[40vh] overflow-y-auto space-y-2">
                {(absHistory?.leaves || []).length === 0 && <div className="text-sm text-muted-foreground">Devamsızlık kaydı yok</div>}
                {(absHistory?.leaves || []).map((l) => (
                  <div key={l.id} className="flex items-center justify-between gap-2 border-b border-border pb-2" data-testid={`abs-hist-${l.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium">{l.label}</div>
                      <div className="text-[11px] text-muted-foreground truncate">{l.start_date} → {l.end_date}{l.reason ? ` · ${l.reason}` : ""}</div>
                    </div>
                    <Badge variant="secondary" className="text-[11px] flex-shrink-0">{l.days} gün</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Card>
        <CardContent className="p-6">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">{t("dash.recent_leaves")}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-muted-foreground text-xs uppercase tracking-wider">
                <tr><th className="text-left py-2">Personel</th><th className="text-left">Tip</th><th className="text-left">Tarih</th><th className="text-left">Gün</th><th className="text-left">Durum</th></tr>
              </thead>
              <tbody>
                {leaves.map((l) => (
                  <tr key={l.id} className="border-t border-border">
                    <td className="py-2">{l.user_name}</td>
                    <td>{l.leave_type}</td>
                    <td>{l.start_date?.slice(0, 10)} → {l.end_date?.slice(0, 10)}</td>
                    <td>{l.day_count}</td>
                    <td><Badge variant={l.final_status === "approved" ? "default" : l.final_status === "rejected" ? "destructive" : "secondary"}>{l.final_status}</Badge></td>
                  </tr>
                ))}
                {leaves.length === 0 && <tr><td colSpan={5} className="py-4 text-center text-muted-foreground">{t("common.no_records")}</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, testid, onClick }) {
  return (
    <button className="stat-card text-left w-full hover:border-primary" data-testid={testid} onClick={onClick} type="button">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
        <Icon size={20} weight="duotone" className="text-accent" />
      </div>
      <div className="font-display text-3xl font-bold mt-2">{value}</div>
    </button>
  );
}
